import * as React from 'react';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import { ToxnotComponent, ToxnotComponentProperties } from '../../core/BaseApplicationComponents/ToxnotComponent';
import { DropdownMenu } from '../../core/DropdownMenu';
import TextInput from '../../core/TextInput';
import { ImportHierarchyOption } from '../../../store/Models/Api/SftpImportModels';

interface ColumnMappingCardLocalProps {
    columnName: string;
    selectedValue: string;
    isReadOnly: boolean;
    onChange: (columnName: string, selectedValue: string) => void;
    mappingOptions: { value: string; label: string }[];
    level?: number | null;
    onLevelChange?: (columnName: string, level: number | null) => void;
    hierarchyOption?: ImportHierarchyOption;
    isInactive?: boolean;
}

type ColumnMappingCardProps = ColumnMappingCardLocalProps & ToxnotComponentProperties;

interface ColumnMappingCardState {
    localHeaderValue: string;
}

class ColumnMappingCardComponent extends ToxnotComponent<ColumnMappingCardProps, ColumnMappingCardState> {
    constructor(props: ColumnMappingCardProps) {
        super(props);
        this.state = {
            localHeaderValue: props.columnName
        };
    }

    componentDidUpdate(prevProps: ColumnMappingCardProps) {
        // Update local state if the prop changed from external source
        if (prevProps.columnName !== this.props.columnName) {
            this.setState({ localHeaderValue: this.props.columnName });
        }
    }

    render() {
        const { columnName, selectedValue, isReadOnly, onChange, mappingOptions, level, onLevelChange, hierarchyOption, isInactive } = this.props;
        const { localHeaderValue } = this.state;
        
        // Show level dropdown only when hierarchy is set to repeated material identifiers
        const showLevelDropdown = hierarchyOption === ImportHierarchyOption.RepeatedMaterialIdentifiers;
        
        // Generate level options from 1 to 11
        const levelOptions = Array.from({ length: 11 }, (_, i) => ({
            key: (i + 1).toString(),
            name: (i + 1).toString()
        }));
        
        // Add "No Level" option at the beginning
        // levelOptions.unshift({ key: '', name: 'No Level' });
        
        // Default level to 1 if no level is set and hierarchy is enabled
        const currentLevel = level !== null && level !== undefined ? level : (showLevelDropdown ? 1 : null);

        // Apply inactive class if the card should be visually distinct
        const containerClass = isInactive ? "new-list-column-edit-container inactive" : "new-list-column-edit-container";

        return (
            <div className={containerClass}>
                <div style={{ position: 'relative' }}>
                    <TextInput
                        additionalClass="header-name"
                        value={localHeaderValue}
                        disabled={true}
                    />
                </div>
                {showLevelDropdown && onLevelChange && (
                    <div>
                        <label>{this.localizeString("Hierarchy Level")}</label>
                        <DropdownMenu
                            options={levelOptions}
                            value={{ 
                                key: currentLevel ? currentLevel.toString() : '1', 
                                name: currentLevel ? currentLevel.toString() : '1'
                            }}
                            onOptionSelected={(option) => {
                                const levelValue = option.key ? parseInt(option.key) : 1;
                                onLevelChange(columnName, levelValue);
                            }}
                            getFriendlyNameForOptions={(option) => option.name}
                            getUniqueValueForOption={(option) => option.key}
                            isDisabled={isReadOnly}
                        />
                    </div>
                )}
                <label>{this.localizeString("Mapping")}</label>
                <DropdownMenu
                    options={mappingOptions.map(option => ({ key: option.value, name: option.label }))}
                    value={{ key: selectedValue, name: mappingOptions.find(o => o.value === selectedValue)?.label || selectedValue }}
                    onOptionSelected={(option) => onChange(columnName, option.key)}
                    getFriendlyNameForOptions={(option) => option.name}
                    getUniqueValueForOption={(option) => option.key}
                    isDisabled={isReadOnly}
                />
            </div>
        );
    }
}

export const ColumnMappingCard = connect(
    (state: ApplicationState) => ({ appServices: state.appServices! })
)(ColumnMappingCardComponent);